import React, { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { Modal, Button, toast } from "@/components/ds";
import { api, formatApiError, type ImportPreview, type ImportResult } from "@/lib/api";

export function ImportContacts({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = async (f: File | null) => {
    setFile(f); setPreview(null); setResult(null);
    if (!f) return;
    setBusy(true);
    try { setPreview(await api.crmImportPreview(f)); }
    catch (e) { toast.error(formatApiError(e)); setFile(null); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.crmImportRun(file);
      setResult(r);
      toast.success(`Imported ${r.created} new, updated ${r.updated}, ${r.leads_created} leads`);
      onImported();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Import contacts & leads"
      description="Upload a CSV or Excel (.xlsx) file with up to 100 rows." testid="import-modal">
      <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
        <a href={api.crmImportTemplateUrl()} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline" data-testid="import-template-link">
          <Download className="h-4 w-4" /> Download CSV template
        </a>

        {!result && (
          <div
            onClick={() => inputRef.current?.click()}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40"
            data-testid="import-dropzone">
            <UploadCloud className="h-8 w-8 text-slate-400" />
            <p className="text-sm font-medium text-slate-700">{file ? file.name : "Click to choose a file"}</p>
            <p className="text-xs text-slate-400">CSV or XLSX · columns: first_name, last_name, phone, email, requested_service, source, estimated_value</p>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden"
              onChange={(e) => pick(e.target.files?.[0] ?? null)} data-testid="import-file-input" />
          </div>
        )}

        {preview && !result && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Valid rows" value={preview.valid} tone="slate" />
              <Stat label="New contacts" value={preview.to_create} tone="green" />
              <Stat label="Will update" value={preview.to_update} tone="blue" />
              <Stat label="Total rows" value={preview.total_rows} tone="slate" />
              <Stat label="Leads created" value={preview.with_lead} tone="green" />
              <Stat label="Errors" value={preview.error_count} tone={preview.error_count ? "red" : "slate"} />
            </div>
            <p className="text-xs text-slate-500">Duplicates (matched by phone or email) are safe-merged — only blank fields are filled, existing values are never overwritten.</p>
            {preview.error_count > 0 && <ErrorList errors={preview.errors} />}
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-emerald-800">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">Imported {result.created} new contacts, updated {result.updated}, created {result.leads_created} leads.</span>
            </div>
            {result.error_count > 0 && <ErrorList errors={result.errors} title={`${result.error_count} row(s) skipped`} />}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose} data-testid="import-close-btn">{result ? "Done" : "Cancel"}</Button>
        {!result && (
          <Button onClick={runImport} disabled={busy || !preview || preview.valid === 0} data-testid="import-confirm-btn">
            <FileSpreadsheet className="h-4 w-4" /> {busy ? "Importing…" : `Import ${preview?.valid ?? 0} row(s)`}
          </Button>
        )}
      </div>
    </Modal>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: "slate" | "green" | "blue" | "red" }) {
  const tones: Record<string, string> = {
    slate: "text-slate-900", green: "text-emerald-700", blue: "text-blue-700", red: "text-red-600",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2 py-3">
      <p className={`text-xl font-bold ${tones[tone]}`} data-testid={`import-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}

function ErrorList({ errors, title = "Rows with issues" }: { errors: { row: number; error: string }[]; title?: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50/50 p-3" data-testid="import-errors">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-red-700"><AlertTriangle className="h-3.5 w-3.5" /> {title}</p>
      <div className="max-h-40 space-y-1 overflow-y-auto">
        {errors.map((e, i) => (
          <p key={i} className="text-xs text-red-600">Row {e.row}: {e.error}</p>
        ))}
      </div>
    </div>
  );
}
